'use client';

/**
 * Shared config, helpers and small components for the onboarding flow
 * walkthrough — a faithful port of the 8-screen PLG prototype
 * (boostt_app_prototype.html) into this app's MUI design system.
 *
 * Every screen renders from the signed-in account's real data (useVoiceSubscription,
 * useVoiceMinutes, useVoiceSuccessFeeSummary, sageVoiceApi) or, for the handful
 * of concepts with no backend endpoint yet, from the placeholder API calls in
 * onboardingDemoApi.ts — never from an inline literal.
 */

import { useMemo } from 'react';
import { Box, Paper, Stack, Typography, Tooltip, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, useTheme, alpha } from '@mui/material';
import { STATUS, DATA_VIZ, SURFACE, ACCENT } from '@/theme/brand-tokens';
import type { BillingActionLogEntry, NotChargedItem } from '@/services/onboardingDemoApi';
import type { VoiceServiceCatalogItem } from '@/types/voice-subscription';

export type { BillingActionLogEntry, NotChargedItem } from '@/services/onboardingDemoApi';

// ── Pricing config — ported verbatim from BOOSTT_PRICING v3.0 ──────────────

export type PlanId = 'launch' | 'scale' | 'enterprise';

export const PLANS: Record<PlanId, {
  name: string; monthly: number; annual: number | null; annualEquivalent: number | null;
  includedMinutes: number; overage: { webrtc: number; pstn: number };
  phoneNumbers: number; extraNumber: number; warmTransfer: boolean; concurrentSessions: number | null;
}> = {
  launch: { name: 'Launch', monthly: 499, annual: 4990, annualEquivalent: 416, includedMinutes: 2000, overage: { webrtc: 0.45, pstn: 0.60 }, phoneNumbers: 1, extraNumber: 8, warmTransfer: false, concurrentSessions: 50 },
  scale: { name: 'Scale', monthly: 1499, annual: 14990, annualEquivalent: 1249, includedMinutes: 8000, overage: { webrtc: 0.35, pstn: 0.48 }, phoneNumbers: 3, extraNumber: 8, warmTransfer: true, concurrentSessions: 500 },
  enterprise: { name: 'Enterprise', monthly: 3999, annual: null, annualEquivalent: null, includedMinutes: 20000, overage: { webrtc: 0.25, pstn: 0.35 }, phoneNumbers: 10, extraNumber: 6, warmTransfer: true, concurrentSessions: null },
};

export const SUCCESS_FEES = {
  callbackScheduled: { launch: 6, scale: 5, enterprise: 3 },
  smsDelivered: { launch: 0.22, scale: 0.18, enterprise: 0.12 },
  emailDelivered: { launch: 0.15, scale: 0.12, enterprise: 0.08 },
  crmWrite: { launch: 0.30, scale: 0.25, enterprise: 0.15 },
  dataLookup: { launch: 0.40, scale: 0.35, enterprise: 0.25 },
  journeySignal: { launch: 0.10, scale: 0.10, enterprise: 0.08 },
};

export const HOME_VERTICAL = {
  booking: { launch: 18, scale: 12, enterprise: 8 },
  transfer: { launch: null as number | null, scale: 25, enterprise: 18 },
};

export const VERTICALS: Record<string, { label: string; short: string; copy: string }> = {
  home: { label: 'Home Services & Auto', short: 'Home Services', copy: 'Roofing, HVAC, Solar, Remodels' },
  insurance: { label: 'Insurance — P&C and Life', short: 'Insurance', copy: 'P&C, Life, Final Expense' },
  medicare: { label: 'Medicare & Health Plans', short: 'Medicare', copy: 'Plan Enrollment and AEP' },
  healthcare: { label: 'Healthcare Appointments', short: 'Healthcare', copy: 'Appointment Scheduling' },
  realestate: { label: 'Real Estate', short: 'Real Estate', copy: 'Seller and Buyer Capture' },
  financial: { label: 'Financial — Wealth & Lending', short: 'Financial', copy: 'Wealth, Lending, Advisory' },
  other: { label: "Other — My Vertical isn't listed", short: 'Other', copy: 'Tell Us What an Outcome is Worth' },
};

export const OTHER_BANDS: Record<string, { label: string; outcomeValue: string; booking: number }> = {
  standard: { label: 'Standard', outcomeValue: 'Under $1,500', booking: 18 },
  elevated: { label: 'Elevated', outcomeValue: '$1,500–$8,000', booking: 28 },
  high: { label: 'High Value', outcomeValue: 'Over $8,000', booking: 38 },
};

export const TRIAL = { freeSessions: 50, memoryHoldDays: 14 };
export const STARTER_PACK_PRICE = 1499;

// Published market-comparison figure (not per-account data) used on the
// trial-recap screen to contrast against an SDR team's cost per booking.
export const SDR_COST_PER_BOOKING_BENCHMARK = 90;

// Modeling default for the plan-sizing projection — how many live data
// lookups a session makes on average. No per-account source exists for
// this; it's a fixed assumption in the cost projection, not account data.
export const LOOKUPS_PER_SESSION_ASSUMPTION = 2;

export const ASSISTED_ONBOARDING_FEES: Record<'launch' | 'other', number> = { launch: 1249, other: 2499 };

export const ONBOARDING_MODES: Record<'self_serve' | 'assisted', { label: string; includes: string[]; excludes: string[] }> = {
  self_serve: {
    label: 'Set it up yourself',
    includes: [
      'Guided in-product setup — vertical template loads pre-trained',
      'Connect your own CRM from the standard connector list',
      'Load your own documents and URLs into the knowledge base',
      'Video library, written playbooks and searchable docs',
      'Email support, 2 business days',
    ],
    excludes: [
      'No named engineer or scheduled sessions',
      'No sandbox validation before you go live',
      'Objection handling starts from the template, not your own calls',
    ],
  },
  assisted: {
    label: 'We set it up with you',
    includes: [
      'Named engineer through go-live',
      'CRM connected for you — field mapping, dedup rules, write-back tested',
      'Agent trained on your real sales calls, not the template',
      'Knowledge base built from your documents',
      'Sandbox campaign run and validated before you go live',
      'Live handover session with your team',
    ],
    excludes: [
      'Additional agents beyond the first ($299 each)',
      'Proprietary integrations ($200/hr)',
      'Compliance script review for regulated verticals ($750)',
    ],
  },
};

// Display copy (summary/includes) and fallback pricing for the Services panel.
// Live price, purchased state and purchase count come from the real catalog
// (useVoiceServiceCatalog → GET /api/voice/services/{tenant_id}) and are merged
// in by service_id at render time — this array is never the source of truth
// for price or purchase state once that catalog has loaded.
export const SERVICES = [
  {
    id: 'activation', label: 'Launch Assist', price: 499, period: 'once',
    summary: 'A specialist connects your CRM, tunes the agent against your real offer and reviews the first 50 live conversations.',
    includes: ['CRM connection and field mapping', 'Agent retrained on your offer and objections', 'Knowledge base built from your documents', 'First-50 conversation review with fixes applied'],
  },
  {
    id: 'compliance_review', label: 'Compliance script review', price: 750, period: 'per script set',
    summary: 'Required before launching in a regulated vertical. Disclosure language reviewed and locked.',
    includes: ['Script set reviewed line by line against the regulation', 'Disclosure language locked to verbatim delivery', 'Prohibited topics configured as hard stops', 'Sign-off document for your compliance file'],
  },
  {
    id: 'extra_agent', label: 'Additional agent training', price: 299, period: 'per agent',
    summary: 'Beyond the agents included in your plan.',
    includes: ['Persona, objective and voice for the new agent', 'Knowledge base delta and objection handling', 'Test conversations reviewed before launch'],
  },
  {
    id: 'sso_scim', label: 'SSO, SAML and SCIM integration', price: 1500, period: 'once',
    summary: 'IdP connection and directory sync. Specialist hours — not waivable.',
    includes: ['IdP connection tested against your directory', 'SCIM provisioning and de-provisioning sync', 'Role mapping from your groups to Boostt roles'],
  },
  {
    id: 'custom_integration', label: 'Custom integration build', price: 200, period: 'per hour',
    summary: 'Proprietary CRM or data source outside the standard connector set.',
    includes: ['Scoped against your endpoint and auth model', 'Built, tested and monitored like a native connector', 'Billed against a written estimate, hourly'],
  },
];

export type AlertId = 'minutes' | 'spend' | 'concurrent' | 'invoice' | 'payment';
export type AlertItem = { id: AlertId; on: boolean; lock: boolean; opts: string[] | null; sel: string | null; t: string; d: string };
export const ALERTS_INITIAL: AlertItem[] = [
  { id: 'minutes', on: true, lock: false, opts: ['50%', '80%', '90%'], sel: '80%', t: 'Included minutes', d: 'Warn me before conversation minutes run past the allowance and overage starts.' },
  { id: 'spend', on: true, lock: false, opts: ['$500', '$1,000', '$2,500', '$5,000'], sel: '$1,000', t: 'Monthly success fees', d: 'Warn me when success fees pass a set amount in a billing period.' },
  { id: 'concurrent', on: false, lock: false, opts: ['70%', '80%', '90%'], sel: '80%', t: 'Simultaneous conversations', d: 'Warn me when the campaign approaches the concurrency ceiling on this plan.' },
  { id: 'invoice', on: true, lock: false, opts: null, sel: null, t: 'Invoice issued', d: 'Email me a copy of each invoice when it closes.' },
  { id: 'payment', on: true, lock: true, opts: null, sel: null, t: 'Payment failed', d: 'Always on. A failed payment pauses live conversations, so this one cannot be turned off.' },
];

export const STEPS = [
  { n: '01', label: 'Activate' },
  { n: '02', label: 'Conversations begin' },
  { n: '03', label: 'Ten left' },
  { n: '04', label: 'Fifty reached' },
  { n: '05', label: 'Your first fifty' },
  { n: '06', label: 'Size a plan' },
  { n: '07', label: 'Subscribed' },
  { n: '08', label: 'Billing' },
] as const;

export const money = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const money0 = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;
export const num = (n: number) => n.toLocaleString('en-US');

// Deterministic seeded shuffle — identical algorithm to the source prototype,
// so the ledger's outcome mix is reproducible across renders.
export function buildLedgerSequence(): string[] {
  const mix: Record<string, number> = { booked: 18, callback: 7, talked: 15, none: 10 };
  const order: string[] = [];
  Object.keys(mix).forEach((k) => { for (let i = 0; i < mix[k]; i++) order.push(k); });
  let seed = 7;
  for (let i = order.length - 1; i > 0; i--) {
    seed = (seed * 1103515245 + 12345) % 2147483648;
    const j = seed % (i + 1);
    [order[i], order[j]] = [order[j], order[i]];
  }
  return order;
}

export function costOn(
  planId: PlanId,
  sessions: number,
  bookPct: number,
  xferPct: number,
  billing: 'monthly' | 'annual',
  avgMinutes: number,
  callbackRatePerSession: number
) {
  const p = PLANS[planId];
  const base = billing === 'annual' && p.annualEquivalent ? p.annualEquivalent : p.monthly;
  const mins = Math.round(sessions * avgMinutes);
  const over = Math.max(0, mins - p.includedMinutes);
  const bookFee = HOME_VERTICAL.booking[planId];
  const xferFee = HOME_VERTICAL.transfer[planId];
  const books = Math.round((sessions * bookPct) / 100);
  const wants = Math.round((sessions * xferPct) / 100);
  const xfers = p.warmTransfer ? wants : 0;
  const cbs = Math.round(sessions * callbackRatePerSession);
  const sms = books + xfers + cbs;
  const emails = books;
  const crm = sessions;
  const look = sessions * LOOKUPS_PER_SESSION_ASSUMPTION;
  const sig = sessions;

  const platformV = base;
  const overageV = over * p.overage.webrtc;
  const bookV = books * bookFee;
  const xferV = xfers * (xferFee ?? 0);
  const callbackV = cbs * SUCCESS_FEES.callbackScheduled[planId];
  const smsV = sms * SUCCESS_FEES.smsDelivered[planId];
  const emailV = emails * SUCCESS_FEES.emailDelivered[planId];
  const crmV = crm * SUCCESS_FEES.crmWrite[planId];
  const lookV = look * SUCCESS_FEES.dataLookup[planId];
  const sigV = sig * SUCCESS_FEES.journeySignal[planId];

  const m1 = platformV;
  const m2 = overageV;
  const m3 = bookV + xferV + callbackV + smsV + emailV + crmV + lookV + sigV;

  return {
    id: planId, p, m1, m2, m3, total: m1 + m2 + m3,
    books, wants, xfers, mins, over, sessions,
    available: !(xferPct > 0 && !p.warmTransfer),
    quote: planId === 'enterprise',
    cpa: books > 0 ? (m1 + m2 + m3) / books : 0,
    lines: {
      overageV, bookV, xferV, callbackV, smsV, emailV, crmV, lookV, sigV,
      cbs, sms, emails, crm, look, sig,
    },
  };
}

export type CostOn = ReturnType<typeof costOn>;

// ── Brand tones — surface hover/active fills + accent wash, per brandbook
// color discipline (accent = interactive/active only, everything else neutral) ──
export function useBrandTones() {
  const theme = useTheme();
  const surfaceTone = theme.palette.mode === 'dark' ? SURFACE.dark : SURFACE.light;
  const accentTone = theme.palette.mode === 'dark' ? ACCENT.dark : ACCENT.light;
  const warningTone = theme.palette.mode === 'dark' ? STATUS.dark.warning : STATUS.light.warning;
  const viz = theme.palette.mode === 'dark' ? DATA_VIZ.dark : DATA_VIZ.light;
  const status = theme.palette.mode === 'dark' ? STATUS.dark : STATUS.light;
  // Outcome → brandbook STATUS / DATA_VIZ (same spirit as conversations/page.tsx).
  // Unknown / empty outcomes cycle the categorical DATA_VIZ palette so the ledger
  // is never a single muted gray bar.
  const realOutcomeColor = (o: string, index = 0) => {
    const key = (o || '').toLowerCase();
    if (key === 'booked' || key === 'appointment_booked') return status.success.text;
    if (key === 'callback' || key === 'callback_scheduled') return status.warning.text;
    if (key === 'qualified') return viz[3].color; // plum
    if (key === 'escalated' || key === 'transferred') return status.danger.text;
    if (key === 'talked') return viz[0].color; // teal
    return viz[index % viz.length].color;
  };
  return { theme, surfaceTone, accentTone, warningTone, realOutcomeColor };
}

export interface RealTrialProps {
  active: boolean;
  severity: 'info' | 'warning' | 'error';
  used: number;
  remaining: number;
  limit: number;
  bookedCount: number;
  callbackCount: number;
  loading: boolean;
  ledgerSessions: Array<{ outcome: string }>;
  canManage: boolean;
  onChoosePlan: () => void;
}

export interface RealBillingProps {
  currentPeriodLabel: string;
  periodAccrued: number;
  platformFeeTotal: number;
  tierLabel: string;
  monthlyFee: number | null;
  overageWebRTC: number | null;
  overagePSTN: number | null;
  includedMinutes: number;
  minutesUsed: number | null;
  overageMinutes: number | null;
  overageUsd: number | null;
  feeLineItems: Array<{ event_type: string; count: number; rate: number; total_usd: number }>;
  onDownloadInvoice: () => void;
  isFetchingCards: boolean;
  card: { brand?: string; last4?: string; exp_month?: number; exp_year?: number } | null;
  onManageCard: () => void;
  company: string | null;
  email: string | null;
  nextBillingDateLabel?: string | null;
  warmTransfer: boolean | null;
  phoneNumbers: number | null;
  actionLog: BillingActionLogEntry[];
  actionLogLoading: boolean;
  notCharged: NotChargedItem[];
  notChargedLoading: boolean;
  /** Real catalog from GET /api/voice/services/{tenant_id} — price, per_unit, repeatable and purchase state per service_id. Null while loading. */
  serviceCatalog?: VoiceServiceCatalogItem[] | null;
  serviceCatalogLoading?: boolean;
  /** Purchases a SERVICES add-on by service_id; quantity applies only to per_unit services. */
  onAddService?: (serviceId: string, quantity?: number) => Promise<void>;
  /** Saved alert preference values (enabled + threshold per AlertId), or null while loading / if never fetched. */
  initialAlerts?: { id: string; enabled: boolean; threshold: string | null }[] | null;
  alertsLoading?: boolean;
  onSaveAlerts?: (alerts: { alert_id: string; enabled: boolean; threshold: string | null }[]) => Promise<void>;
}

// Trial-activity counters with no per-account backend source yet (SMS/CRM
// counts during the trial, how many recipient profiles the agent is holding,
// total trial minutes) — see fetchTrialActivityExtras in onboardingDemoApi.ts.
export interface TrialActivitySnapshot {
  smsConfirmations: number | null;
  crmRecordsWritten: number | null;
  recipientMemoryCount: number | null;
  totalMinutes: number | null;
  avgSessionMinutes: number | null;
  loading: boolean;
}

// Live processing-queue depth — see fetchQueueStatus in onboardingDemoApi.ts.
export interface QueueStatusProps {
  totalWaiting: number | null;
  handled: number | null;
  remaining: number | null;
  recentBookings: number | null;
  recentBookingWindowMinutes: number | null;
  loading: boolean;
}

// Persisted plan-sizing inputs plus the real saved card — see
// fetchPlanSizingBenchmarks / savePlanSizing in onboardingDemoApi.ts.
export interface PlanSizingProps {
  audiencePerMonth: number | null;
  responseRatePct: number | null;
  bookPct: number | null;
  transferPct: number | null;
  isSaved: boolean;
  avgSessionMinutes: number | null;
  loading: boolean;
  card: { brand?: string; last4?: string; exp_month?: number; exp_year?: number } | null;
  isFetchingCards: boolean;
  /** Card OR verified ACH on file — whichever this account would actually be billed with. */
  hasPaymentMethod: boolean;
  onSave?: (sizing: {
    audiencePerMonth: number;
    responseRatePct: number;
    bookPct: number;
    transferPct: number;
  }) => Promise<void>;
}

function ledgerTickHeight(outcome: string): number {
  const key = (outcome || '').toLowerCase();
  if (key === 'booked' || key === 'appointment_booked') return 100;
  if (key === 'callback' || key === 'callback_scheduled') return 78;
  if (key === 'qualified' || key === 'talked') return 58;
  if (key === 'escalated' || key === 'transferred') return 70;
  return 42;
}

export function TickLedger({
  used, total, height = 40, sessions, colorFor: colorForProp,
}: {
  used: number; total: number; height?: number;
  sessions?: Array<{ outcome: string }>;
  colorFor?: (outcome: string, index?: number) => string;
}) {
  const theme = useTheme();
  const viz = theme.palette.mode === 'dark' ? DATA_VIZ.dark : DATA_VIZ.light;
  const status = theme.palette.mode === 'dark' ? STATUS.dark : STATUS.light;
  const seq = useMemo(() => buildLedgerSequence(), []);
  const defaultColorFor = (o: string, index = 0) => {
    const key = (o || '').toLowerCase();
    if (key === 'booked' || key === 'appointment_booked') return status.success.text;
    if (key === 'callback' || key === 'callback_scheduled') return status.warning.text;
    if (key === 'qualified') return viz[3].color;
    if (key === 'escalated' || key === 'transferred') return status.danger.text;
    if (key === 'talked') return viz[0].color;
    return viz[index % viz.length].color;
  };
  const colorFor = colorForProp ?? defaultColorFor;
  return (
    <Stack direction="row" spacing={0.5} alignItems="flex-end" sx={{ height }}>
      {Array.from({ length: total }).map((_, i) => {
        if (i >= used) {
          return (
            <Box
              key={i}
              sx={{
                flex: 1, height: '100%', borderRadius: 0.5,
                border: '1px dashed', borderColor: 'divider',
                backgroundImage: `repeating-linear-gradient(135deg, ${alpha(theme.palette.text.primary, 0.06)} 0 3px, transparent 3px 6px)`,
              }}
            />
          );
        }
        const o = sessions ? (sessions[i]?.outcome || 'none') : (seq[i] ?? 'none');
        const pct = ledgerTickHeight(o);
        const tick = (
          <Box key={i} sx={{ flex: 1, height: '100%', display: 'flex', alignItems: 'flex-end' }}>
            <Box sx={{ width: '100%', height: `${pct}%`, borderRadius: 0.5, bgcolor: colorFor(o, i) }} />
          </Box>
        );
        return sessions ? <Tooltip key={i} title={o || 'Completed'}>{tick}</Tooltip> : tick;
      })}
    </Stack>
  );
}

export function StatTile({ label, value, detail, highlight }: { label: string; value: string | number; detail?: string; highlight?: boolean }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '10px' }}>
      <Typography variant="caption" color="text.primary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 11, fontFamily: 'var(--font-mono)' }}>
        {label}
      </Typography>
      <Typography variant="h4" fontWeight={700} color={highlight ? 'primary.main' : 'text.primary'} sx={{ mt: 0.5, fontFamily: 'var(--font-mono)' }}>
        {value}
      </Typography>
      {detail && (
        <Typography variant="caption" color="text.primary">{detail}</Typography>
      )}
    </Paper>
  );
}

// Real cost-element breakdown, shared by StepSubscribed (07) and StepBilling
// (08) so both screens read the same numbers from the same source instead of
// each computing their own (and risking disagreement).
export function BillingBreakdownTable({ realBilling }: { realBilling: RealBillingProps }) {
  const { surfaceTone } = useBrandTones();
  const totalBorderColor = 'divider';
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Cost element</TableCell>
            <TableCell align="right">Quantity</TableCell>
            <TableCell align="right">Rate</TableCell>
            <TableCell align="right">Amount</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell colSpan={4} sx={{ bgcolor: surfaceTone.hover, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>1 · Platform fee — billed in advance</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              {realBilling.tierLabel} subscription
              <Typography variant="caption" color="text.primary" display="block">{realBilling.currentPeriodLabel}</Typography>
            </TableCell>
            <TableCell align="right">1 mo</TableCell>
            <TableCell align="right">{realBilling.monthlyFee != null ? `$${realBilling.monthlyFee.toLocaleString()}/mo` : '—'}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>${realBilling.platformFeeTotal.toLocaleString()}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell colSpan={4} sx={{ bgcolor: surfaceTone.hover, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>2 · Conversation minutes — {realBilling.currentPeriodLabel}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              Included minutes
              <Typography variant="caption" color="text.primary" display="block">
                Overage billed at ${realBilling.overageWebRTC}/min (WebRTC), ${realBilling.overagePSTN}/min (PSTN) once the allowance is exceeded
              </Typography>
            </TableCell>
            <TableCell align="right">
              {realBilling.minutesUsed != null ? realBilling.minutesUsed.toLocaleString() : '—'} / {realBilling.includedMinutes.toLocaleString()} min
            </TableCell>
            <TableCell align="right">Included</TableCell>
            <TableCell align="right" sx={{ color: 'success.main' }}>$0.00</TableCell>
          </TableRow>
          {!!realBilling.overageMinutes && realBilling.overageMinutes > 0 && (
            <TableRow>
              <TableCell>
                Overage minutes
                <Typography variant="caption" color="text.primary" display="block">Beyond the included allowance this period</Typography>
              </TableCell>
              <TableCell align="right">{realBilling.overageMinutes.toLocaleString()} min</TableCell>
              <TableCell align="right">Blended rate</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>${(realBilling.overageUsd ?? 0).toFixed(2)}</TableCell>
            </TableRow>
          )}
          {/* Success fees — commented out per BOOSTT request
          <TableRow>
            <TableCell colSpan={4} sx={{ bgcolor: surfaceTone.hover, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>3 · Success fees — on confirmed completion only</TableCell>
          </TableRow>
          {realBilling.feeLineItems.length > 0 ? (
            realBilling.feeLineItems.map((row) => (
              <TableRow key={row.event_type}>
                <TableCell sx={{ textTransform: 'capitalize' }}>{row.event_type.replace(/_/g, ' ')}</TableCell>
                <TableCell align="right">{row.count}</TableCell>
                <TableCell align="right">${row.rate.toFixed(4)} each</TableCell>
                <TableCell align="right">${row.total_usd.toFixed(2)}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={4} sx={{ color: 'text.primary', fontStyle: 'italic' }}>No completed actions yet this period</TableCell>
            </TableRow>
          )}
          */}
          <TableRow>
            <TableCell sx={{ fontWeight: 700, borderTop: '1px solid', borderColor: totalBorderColor }}>Accrued this period</TableCell>
            <TableCell sx={{ borderTop: '1px solid', borderColor: totalBorderColor }} />
            <TableCell sx={{ borderTop: '1px solid', borderColor: totalBorderColor }} />
            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1.05rem', borderTop: '1px solid', borderColor: totalBorderColor }}>
              {money(realBilling.periodAccrued)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
